import React from "react";
import { Avatar, Box, FormControl, InputLabel, MenuItem, Select, Typography } from "@mui/material";
import InviteCardShell from "./InviteCardShell";
import InviteBodySlot from "./InviteBodySlot";
import InviteErrorSlot from "./InviteErrorSlot";
import InviteLinkedAccountRow from "./InviteLinkedAccountRow";
import InviteConnectActions from "./InviteConnectActions";
import InviteDisconnectButton from "./InviteDisconnectButton";
import {
  InviteFatalAction,
  InviteRevalidatingAction,
} from "./InviteActionHelpers";
import { getInviteHeroVariant } from "./inviteCompanyImage";
import { getInviteSubtitle, getInviteTitle } from "./inviteCopy";
import { getInviteSectionVisibility } from "./inviteSectionConfig";
import { inviteTextSx } from "./inviteTypography";
import InviteMetaPodcastSubtitle from "./InviteMetaPodcastSubtitle";
import { isInviteAlreadyConnectedPhase, isPrixiInvitePhase } from "./invitePhaseUtils";
import type {
  InviteData,
  InvitePlatform,
  InviteUnavailableReason,
  InviteViewPhase,
} from "./inviteTypes";
import type { MetaIgLinkedAsset, MetaMappingRow, CompanyPodcastOption } from "./metaConnectUtils";

export type MetaConnectedAccount = {
  igUsername?: string;
  igId: string;
  igProfilePictureUrl?: string;
  podcastTitle: string;
  podcastId: string;
  podcastImage?: string;
};

export interface InviteCardViewProps {
  viewPhase: InviteViewPhase;
  inviteData?: InviteData | null;
  platform?: InvitePlatform;
  unavailableReason?: InviteUnavailableReason | null;
  fatalError?: string | null;
  successParam?: string | null;
  disconnectedParam?: string | null;
  isPlatformSuccess?: boolean;
  linkedAccount?: {
    platform: InvitePlatform;
    accountId?: string;
    username?: string;
    profileUrl?: string;
  } | null;
  linkedAccountLoading?: boolean;
  metaConnectedAccounts?: MetaConnectedAccount[];
  facebookError?: string | null;
  youtubeError?: string | null;
  tiktokError?: string | null;
  spotifyError?: string | null;
  disconnectError?: string | null;
  termsAccepted?: boolean;
  onTermsChange?: (accepted: boolean) => void;
  facebookLoading?: boolean;
  facebookAccessToken?: string | null;
  metaNeedsPageId?: boolean;
  metaPageId?: string;
  onMetaPageIdChange?: (value: string) => void;
  metaSubmitting?: boolean;
  companyPodcasts?: CompanyPodcastOption[];
  targetPodcastId?: string;
  targetPodcastTitle?: string;
  selectedPodcastIds?: string[];
  onSelectedPodcastIdsChange?: (ids: string[]) => void;
  metaAssets?: MetaIgLinkedAsset[];
  metaMappings?: MetaMappingRow[];
  onMetaMappingsChange?: (mappings: MetaMappingRow[]) => void;
  onMetaContinueToFacebook?: () => void;
  onMetaFinalize?: () => void;
  onMetaMappingBack?: () => void;
  spotifyUrl?: string;
  onSpotifyUrlChange?: (value: string) => void;
  spotifyLoading?: boolean;
  disconnectLoading?: boolean;
  onMetaConnect?: () => void;
  onMetaPageIdSubmit?: () => void;
  onYouTubeConnect?: () => void;
  onTikTokConnect?: () => void;
  onSpotifyConnect?: () => void;
  onDisconnect?: () => void;
  onRetry?: () => void;
  showRetry?: boolean;
  interactive?: boolean;
}

const noop = () => {};

export const InviteCardView: React.FC<InviteCardViewProps> = ({
  viewPhase,
  inviteData,
  platform: platformProp,
  unavailableReason = null,
  fatalError = null,
  successParam = null,
  disconnectedParam = null,
  isPlatformSuccess = false,
  linkedAccount = null,
  linkedAccountLoading = false,
  metaConnectedAccounts,
  facebookError = null,
  youtubeError = null,
  tiktokError = null,
  spotifyError = null,
  disconnectError = null,
  termsAccepted = false,
  onTermsChange = noop,
  facebookLoading = false,
  facebookAccessToken = null,
  metaNeedsPageId = false,
  metaPageId = "",
  onMetaPageIdChange = noop,
  metaSubmitting = false,
  companyPodcasts = [],
  targetPodcastId,
  targetPodcastTitle,
  selectedPodcastIds = [],
  onSelectedPodcastIdsChange = noop,
  metaAssets = [],
  metaMappings = [],
  onMetaMappingsChange = noop,
  onMetaContinueToFacebook = noop,
  onMetaFinalize = noop,
  onMetaMappingBack = noop,
  spotifyUrl = "",
  onSpotifyUrlChange = noop,
  spotifyLoading = false,
  disconnectLoading = false,
  onMetaConnect = noop,
  onMetaPageIdSubmit = noop,
  onYouTubeConnect = noop,
  onTikTokConnect = noop,
  onSpotifyConnect = noop,
  onDisconnect = noop,
  onRetry = noop,
  showRetry = true,
  interactive = true,
}) => {
  const platform: InvitePlatform =
    platformProp ?? inviteData?.platform ?? "youtube";

  const integrationSource =
    inviteData?.integrationSource ??
    (isPrixiInvitePhase(viewPhase) ? "prixi" : "podscope");

  const sections = getInviteSectionVisibility(viewPhase, {
    isPlatformSuccess,
  });

  const title = getInviteTitle(viewPhase, {
    successParam,
    disconnectedParam,
    unavailableReason,
    platform,
  });

  const subtitle = getInviteSubtitle(viewPhase, {
    createdBy: inviteData?.createdBy ?? "",
    podcastTitle: inviteData?.podcastTitle,
    companyName: inviteData?.companyName,
    fatalError,
    unavailableReason,
    platform,
  });

  const showMetaPodcastSubtitle =
    platform === "meta" &&
    (viewPhase === "meta_podcast_select" ||
      viewPhase === "prixi_meta_podcast_select" ||
      viewPhase === "meta_mapping" ||
      viewPhase === "prixi_meta_mapping");

  const metaPodcastSubtitleInteractive =
    (viewPhase === "meta_podcast_select" ||
      viewPhase === "prixi_meta_podcast_select") &&
    !!inviteData?.metaInviteMultiPodcastEnabled;

  const subtitleSlot =
    showMetaPodcastSubtitle && companyPodcasts.length > 0 ? (
      <InviteMetaPodcastSubtitle
        inviterName={inviteData?.companyName || inviteData?.createdBy || "Podscope"}
        targetPodcastId={targetPodcastId ?? inviteData?.podcastId}
        targetPodcastTitle={
          targetPodcastTitle ??
          inviteData?.targetPodcast?.title ??
          inviteData?.podcastTitle
        }
        podcasts={companyPodcasts}
        selectedPodcastIds={selectedPodcastIds}
        onSelectedPodcastIdsChange={
          interactive && metaPodcastSubtitleInteractive
            ? onSelectedPodcastIdsChange
            : undefined
        }
        interactive={interactive && metaPodcastSubtitleInteractive}
      />
    ) : undefined;

  const heroVariant = getInviteHeroVariant(viewPhase);

  const showOAuthErrors = sections.errors === "oauth";
  const showDisconnectErrors = sections.errors === "disconnect";
  const hasOAuthError = !!(
    facebookError ||
    youtubeError ||
    tiktokError ||
    spotifyError
  );
  const hasDisconnectError = !!disconnectError;

  const renderActionSlot = () => {
    switch (sections.actions) {
      case "skeleton":
        return null;
      case "spinner":
        return <InviteRevalidatingAction />;
      case "retry":
        return (
          <InviteFatalAction
            showRetry={showRetry}
            onRetry={interactive ? onRetry : undefined}
          />
        );
      case "disconnect":
        return (
          <InviteDisconnectButton
            variant={
              isInviteAlreadyConnectedPhase(viewPhase)
                ? "already_connected"
                : "success"
            }
            disconnectLoading={disconnectLoading}
            onDisconnect={interactive ? onDisconnect : noop}
            disabled={!interactive}
          />
        );
      case "connect":
        return (
          <InviteConnectActions
            platform={platform}
            phase={viewPhase}
            termsAccepted={termsAccepted}
            onTermsChange={interactive ? onTermsChange : noop}
            facebookLoading={facebookLoading}
            facebookAccessToken={facebookAccessToken}
            metaNeedsPageId={metaNeedsPageId}
            metaPageId={metaPageId}
            onMetaPageIdChange={interactive ? onMetaPageIdChange : noop}
            metaSubmitting={metaSubmitting}
            companyPodcasts={companyPodcasts.length > 0 ? companyPodcasts : inviteData?.companyPodcasts}
            selectedPodcastIds={selectedPodcastIds}
            metaAssets={metaAssets}
            metaMappings={metaMappings}
            onMetaMappingsChange={onMetaMappingsChange}
            onMetaContinueToFacebook={onMetaContinueToFacebook}
            onMetaFinalize={onMetaFinalize}
            onMetaMappingBack={onMetaMappingBack}
            spotifyUrl={spotifyUrl}
            onSpotifyUrlChange={interactive ? onSpotifyUrlChange : noop}
            spotifyLoading={spotifyLoading}
            onMetaConnect={interactive ? onMetaConnect : noop}
            onMetaPageIdSubmit={interactive ? onMetaPageIdSubmit : noop}
            onYouTubeConnect={interactive ? onYouTubeConnect : noop}
            onTikTokConnect={interactive ? onTikTokConnect : noop}
            onSpotifyConnect={interactive ? onSpotifyConnect : noop}
          />
        );
      default:
        return null;
    }
  };

  return (
    <InviteCardShell
      isSkeleton={viewPhase === "loading"}
      companyImageS3Key={inviteData?.companyImageS3Key}
      companyImageUrl={inviteData?.companyImageUrl}
      companyName={inviteData?.companyName}
      integrationSource={integrationSource}
      heroVariant={heroVariant}
      title={title}
      subtitle={showMetaPodcastSubtitle ? null : subtitle}
      subtitleSlot={subtitleSlot}
      showAccount={sections.account || sections.accountSkeleton}
      linkedAccountSlot={
        sections.account || sections.accountSkeleton ? (
          metaConnectedAccounts && metaConnectedAccounts.length > 1 ? (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1, width: "100%" }}>
              {metaConnectedAccounts.map((acc) => {
                const igLabel = acc.igUsername
                  ? `@${acc.igUsername.replace(/^@+/, "")}`
                  : acc.igId;
                const igInitial = (acc.igUsername ?? acc.igId).trim().charAt(0).toUpperCase();
                const podInitial = acc.podcastTitle.trim().charAt(0).toUpperCase();
                return (
                  <Box
                    key={acc.podcastId}
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      gap: 1.5,
                      p: 1.5,
                      border: "1px solid",
                      borderColor: "divider",
                      borderRadius: 1,
                    }}
                  >
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Avatar
                        src={acc.podcastImage}
                        alt={acc.podcastTitle}
                        sx={{
                          width: 28,
                          height: 28,
                          borderRadius: 0.5,
                          fontSize: "0.75rem",
                          bgcolor: "common.black",
                          border: "1px solid",
                          borderColor: "divider",
                        }}
                      >
                        {podInitial}
                      </Avatar>
                      <Typography variant="body2" sx={{ ...inviteTextSx, fontWeight: 600 }}>
                        {acc.podcastTitle}
                      </Typography>
                    </Box>
                    <FormControl fullWidth size="small" disabled>
                      <InputLabel shrink>{`Cuenta de Instagram`}</InputLabel>
                      <Select
                        label="Cuenta de Instagram"
                        value="selected"
                        notched
                        sx={{
                          "& .MuiSelect-select": { color: "text.primary" },
                          "&.Mui-disabled .MuiSelect-select": {
                            WebkitTextFillColor: "inherit",
                            color: "text.primary",
                          },
                        }}
                        renderValue={() => (
                          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                            <Avatar
                              src={acc.igProfilePictureUrl}
                              alt={acc.igUsername ?? acc.igId}
                              sx={{
                                width: 22,
                                height: 22,
                                fontSize: "0.65rem",
                                bgcolor: "common.black",
                                border: "1px solid",
                                borderColor: "divider",
                              }}
                            >
                              {igInitial}
                            </Avatar>
                            {igLabel}
                          </Box>
                        )}
                      >
                        <MenuItem value="selected">{igLabel}</MenuItem>
                      </Select>
                    </FormControl>
                  </Box>
                );
              })}
            </Box>
          ) : (
            <InviteLinkedAccountRow
              platform={linkedAccount?.platform ?? platform}
              username={linkedAccount?.username}
              accountId={linkedAccount?.accountId}
              profileUrl={linkedAccount?.profileUrl}
              loading={linkedAccountLoading || sections.accountSkeleton}
            />
          )
        ) : null
      }
      bodySlot={
        sections.body.length > 0 ? (
          <InviteBodySlot
            kinds={sections.body}
            platform={platform}
            integrationSource={integrationSource}
            podcastTitle={inviteData?.podcastTitle}
            companyName={inviteData?.companyName}
          />
        ) : null
      }
      errorSlot={
        (showOAuthErrors && hasOAuthError) ||
        (showDisconnectErrors && hasDisconnectError) ? (
          <InviteErrorSlot
            facebookError={showOAuthErrors ? facebookError : null}
            youtubeError={showOAuthErrors ? youtubeError : null}
            tiktokError={showOAuthErrors ? tiktokError : null}
            spotifyError={showOAuthErrors ? spotifyError : null}
            disconnectError={showDisconnectErrors ? disconnectError : null}
          />
        ) : null
      }
      actionSlot={renderActionSlot()}
    />
  );
};

export default InviteCardView;
